import styled from 'styled-components'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { BodyLarge, BodySmall, BodySmallMedium, Title2, Title4 } from '../../../components/styles/Typography'
import { theme } from '../../../theme/theme'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { ChatPlus, Stethoscope, QuestionMark } from '../../../components/icons'
import { OutlinedTag } from '../../../components/Tag'
import { useGetUserCasesQuery } from '../../../api/ester-b2b-api/private/case/caseQueries'
import { CaseTable } from '../../../components/CaseTable'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isMobile } from '../../../utils/devices'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { PreviewGuard } from '../../../components/PreviewGuard'

export const Messages = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: cases, isLoading, isError } = useGetUserCasesQuery()
  const width = useWindowWidth()
  const isMobileDevice = isMobile(width)
  const topicTags = [
    t('employee.messages.topicDiet'),
    t('employee.messages.topicSleep'),
    t('employee.messages.topicStress'),
    t('employee.messages.topicMentalHealth'),
    t('employee.messages.topicOutcomes'),
    t('employee.messages.topicDetails'),
    t('employee.messages.topicSelfCare'),
  ]

  const casesArray = Array.isArray(cases) ? cases : []
  const hasCases = casesArray.length > 0

  const handleCaseClick = (caseId: string) => {
    navigate(`/portal/messages/${caseId}`)
  }

  if (isLoading) return <LoadingSpinner />
  if (isError) return <PreviewGuard />

  return (
    <>
      <ComponentLayout
        paddingTop={isMobileDevice ? theme.spacing.large : theme.spacing.xxlarge}
        paddingLeft={theme.spacing.large}
        paddingRight={theme.spacing.large}
      >
        <HeaderContent>
          <Title2>{t('employee.messages.title')}</Title2>
          {!isMobileDevice && (
            <OnClickButton
              text={t('employee.messages.startConversation')}
              onClick={() => navigate('/portal/messages/new')}
              icon={<ChatPlus size={16} color={theme.color.white} />}
              buttonStyle={{ flexDirection: 'row-reverse', gap: `${theme.spacing.xsmall}px` }}
            />
          )}
        </HeaderContent>
      </ComponentLayout>
      {hasCases ? (
        <TableContainer>
          <CaseTable cases={casesArray} onCaseClick={handleCaseClick} isMobile={isMobileDevice} />
          <SelectConversationContainer>
            <IconWrapper>
              <QuestionMark size={40} color={theme.color.plum} />
            </IconWrapper>
            <StyledLink to="/portal/support">
              <BodySmall>
                <Trans
                  i18nKey="employee.messages.selectConversationFooter"
                  components={{
                    highlight: <HighlightedText />,
                  }}
                />
              </BodySmall>
            </StyledLink>
          </SelectConversationContainer>
          {isMobileDevice && (
            <MobileButtonContainer>
              <OnClickButton
                text={t('employee.messages.startConversation')}
                onClick={() => navigate('/portal/messages/new')}
                icon={<ChatPlus size={16} color={theme.color.white} />}
                buttonStyle={{ flexDirection: 'row-reverse', gap: `${theme.spacing.xsmall}px` }}
              />
            </MobileButtonContainer>
          )}
        </TableContainer>
      ) : (
        <MainContainer>
          <EmptyStateContent>
            <IconWrapper>
              <Stethoscope size={40} color={theme.color.plum} />
            </IconWrapper>
            <TextContent>
              <Title4>{t('employee.messages.emptyTitle')}</Title4>
              <BodyLarge>{t('employee.messages.emptyDescription')}</BodyLarge>
            </TextContent>
            <OnClickButton
              text={t('employee.messages.startConversation')}
              onClick={() => navigate('/portal/messages/new')}
              icon={<ChatPlus size={16} color={theme.color.white} />}
              buttonStyle={{ flexDirection: 'row-reverse', gap: `${theme.spacing.xsmall}px` }}
            />
            <TopicsSection>
              <TopicsLabel>{t('employee.messages.topicsLabel')}</TopicsLabel>
              <TagsContainer>
                {topicTags.map((tag, index) => (
                  <OutlinedTag key={index} text={tag} />
                ))}
              </TagsContainer>
            </TopicsSection>
          </EmptyStateContent>
        </MainContainer>
      )}
    </>
  )
}

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.mediumLarge}px;
`

const MainContainer = styled.div`
  border-top: 1px solid ${theme.color.beigeDark};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 600px;
`

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.mediumLarge}px;
  padding: ${theme.spacing.xlarge}px 0;
  margin: 0 ${theme.spacing.large}px;
  max-width: 490px;
  width: 100%;
  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    flex: 1;
  }
`

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(91, 25, 52, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  text-align: center;
  width: 100%;
  padding: 0 5px;
`

const TopicsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding-top: ${theme.spacing.mediumLarge}px;
  border-top: 1px solid rgba(34, 34, 34, 0.1);
  max-width: 448px;
  width: 100%;
`

const TopicsLabel = styled(BodySmallMedium)`
  color: ${theme.color.black};
  letter-spacing: 0.4px;
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.xsmall}px ${theme.spacing.medium}px;
  padding: 0 ${theme.spacing.medium}px;
`

const TableContainer = styled.div`
  border-top: 1px solid ${theme.color.beigeDark};
  width: 100%;
  margin-top: ${theme.spacing.mediumLarge}px;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    margin-top: 0;
  }
`

const SelectConversationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xsmall}px;
  padding: ${theme.spacing.large}px;
  text-align: center;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    gap: ${theme.spacing.medium}px;
    padding: ${theme.spacing.xxlarge}px ${theme.spacing.large}px;
  }
`

const StyledLink = styled(Link)`
  color: ${theme.color.plum};
`

const HighlightedText = styled.span`
  color: ${theme.color.plumMid};
`

const MobileButtonContainer = styled.div`
  padding-bottom: ${theme.spacing.medium}px;
  display: flex;
  justify-content: center;
`
